import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { getCaseIntake, getCaseJourneys } from '@brands/services/cases/multiStage';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { DentalHistory, MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { getOneReportMedia } from '@brands/services/oneReport/getOneReportMedia';
import { AppDispatch } from '@brands/store/store';
import { clearLinkedCase, PreviousClinicalData, setLinkedCase } from '@brands/store/slices/linkedCaseSlice';
import { isIncludedCareCoordination } from '@brands/Dashboard/MultiStage/utils/careCoordinationExemption';
import { FormValues, setForm, UploadedFile } from '@brands/store/slices/patientFormSlice';

// Finds the case immediately before `actionCase` in the same journey, so its
// answers can seed the new linked case's intake: the journey containing
// `actionCase` (found by scanning for its id, not by trusting
// `actionCase.case_journey_id` — not every caller's case object has that
// field populated, e.g. UpcomingServices.tsx's cards come from listAllCases,
// which omits it), then the most recently created case in that journey from
// before `actionCase` itself. journey_sequence_order isn't used — nothing
// populates it reliably — created_at is the actual ground truth for order.
export const findPreviousJourneyCase = async (actionCase: ICase): Promise<ICase | null> => {
  const journeys = await getCaseJourneys(actionCase.patient_id);
  // Compared as strings — getCaseJourneys' ids don't reliably come back as
  // the same JS type as actionCase.id (a recurring shape mismatch between
  // this app's endpoints), and a `!==`/`===` on mismatched number/string ids
  // silently never matches, which is what let actionCase slip through its
  // own "exclude the current case" filter below and come back as its own
  // previousCase.
  const actionCaseId = String(actionCase.id);
  const journey = journeys.multi_stage.find((j) => j.cases.some((c) => String(c.id) === actionCaseId));
  if (!journey) return null;

  const actionCreatedAt = new Date(actionCase.created_at).getTime();
  const priorCases = journey.cases.filter(
    (c) => String(c.id) !== actionCaseId && new Date(c.created_at).getTime() < actionCreatedAt,
  );
  if (!priorCases.length) return null;

  const lastCreated = priorCases.reduce((latest, c) =>
    new Date(c.created_at).getTime() > new Date(latest.created_at).getTime() ? c : latest,
  );

  // Belt-and-suspenders: never hand back the case being completed as its own
  // "previous" case, however that might happen.
  if (String(lastCreated.id) === actionCaseId) return null;

  try {
    return await getOneCase(lastCreated.id);
  } catch {
    return null;
  }
};

// Only payment/insurance (19) carries over: it maps cleanly onto the wizard's
// own FormValues shape and just seeds a starting point the patient still
// reviews while walking the wizard normally, without skipping a step. The
// dental concerns answers (16) are deliberately left out: emergency, pain and
// reason for visit describe this visit, not the previous one, so the patient
// answers that page fresh instead of seeing the previous case's selections.
// Provider/timeslot and need-category are left out too: a follow-up case needs
// its own appointment slot, and there's no source data for the latter on a
// prior case anyway. The case type is never inherited: it's always the NEW
// linked case's type.
export const buildPrefillFormValues = (previousCase: ICase | null, newCaseType: string): FormValues[] => {
  const values: FormValues[] = [{ form_question_id: 20, value: { caseType: newCaseType } }];
  if (!previousCase) return values;

  values.push({
    form_question_id: 19,
    value: {
      paymentId: previousCase.payment_profile?.id ? String(previousCase.payment_profile.id) : '',
      insuranceId: previousCase.insurance_id ? String(previousCase.insurance_id) : '',
      secondaryInsuranceId: previousCase.insurance?.is_secondary ? String(previousCase.insurance.id) : '',
      addSecondaryInsurance: !!previousCase.insurance?.is_secondary,
      dataWithUpdatedInsurance: previousCase.insurance,
      medicaidAnswer: previousCase.insurance?.state_funded_coverage_status || '',
      usedInsuranceId: previousCase.insurance_id,
      payWithCredit: !!previousCase.paid_with_credits,
    },
  });

  return values;
};

// Carries over any images already uploaded to the previous linked case, so the
// patient isn't asked to re-photograph the same thing for a follow-up. Each
// one_report media entry only has a media_id — resolve filename/mimetype so
// the images step can display them the same way freshly-uploaded ones look.
// A failed lookup falls back to a generic label rather than dropping the
// attachment: filename/mimetype are cosmetic here — UploadedFileImage
// independently re-resolves the actual preview straight from media_id at
// render time, the same way it already does for freshly-uploaded images in
// a fresh session — so losing the metadata call shouldn't lose the image.
export const buildPrefillImages = async (previousCase: ICase | null): Promise<UploadedFile[]> => {
  const media = previousCase?.one_report?.media?.filter((item) => item.media_id) ?? [];
  if (!media.length) return [];

  return Promise.all(
    media.map(async (item, index): Promise<UploadedFile> => {
      const mediaId = item.media_id as string;
      try {
        const resolved = await getOneReportMedia(mediaId);
        return { name: resolved.filename, media_id: mediaId, mimetype: resolved.mimetype };
      } catch {
        return { name: `Attachment ${index + 1}`, media_id: mediaId };
      }
    }),
  );
};

// Pulls the individual fields back out of the previous linked case's already-
// snapshotted medical_history_snapshot/medical_conditions_snapshot/
// dental_history_snapshot (same key names as the clinical_data records they
// were snapshotted from — see ClinicalDataStepOne.tsx etc.) so they can be
// written onto the patient's own clinical_data profile (applyPreviousClinicalData
// in ConfirmPayment.tsx) rather than copied onto the case as an opaque blob.
export const extractPreviousClinicalData = (previousCase: ICase | null): PreviousClinicalData | null => {
  if (!previousCase) return null;

  const medicalHistory = (previousCase.medical_history_snapshot as MedicalHistory | null | undefined) || null;
  const medicalConditions = (previousCase.medical_conditions_snapshot as MedicalConditions | null | undefined) || null;
  const dentalHistory = (previousCase.dental_history_snapshot as DentalHistory | null | undefined) || null;

  if (!medicalHistory && !medicalConditions && !dentalHistory) return null;

  return { medicalHistory, medicalConditions, dentalHistory };
};

// The single entry point for every "Complete Intake" action on an upcoming
// linked case: seeds the wizard's Redux form from the previous case in the
// journey, records which case is being completed (so SelectPatient can lock
// the picker and ConfirmPayment can patch instead of create), then starts the
// patient at the top of the normal intake wizard.
export const startLinkedCaseCompletion = async (
  actionCase: ICase,
  dispatch: AppDispatch,
  roleNavigate: ReturnType<typeof useRoleNavigate>,
): Promise<void> => {
  const isCareCoordination = actionCase.type === CaseType.care_coordination;
  // Other case types always need prefill, so overlap their independent lookups.
  // Defer CC journey reads until we know this is not an exempt follow-up.
  const previousCasePromise = isCareCoordination ? null : findPreviousJourneyCase(actionCase).catch(() => null);
  // Upcoming roots also enter this flow. Only exempt confirmed follow-ups.
  const intakeState = await getCaseIntake(actionCase.id).catch(() => null);
  // A care coordination follow-up is free and has no intake pages, but the
  // patient still has to see the Jotform link before it can be assigned, so
  // it skips straight to the popup that ends every CC intake.
  if (isCareCoordination && intakeState?.is_linked_care_coordination === true) {
    dispatch(clearLinkedCase());
    roleNavigate(intakeState.needs_intake ? `/care-coordination-form/${actionCase.id}` : '/my-care');
    return;
  }
  const previousCase = await (previousCasePromise ?? findPreviousJourneyCase(actionCase).catch(() => null));
  // The same exemption, for a follow-up the server hasn't flagged as linked
  // care coordination.
  if (previousCase && isIncludedCareCoordination(actionCase, [previousCase])) {
    dispatch(clearLinkedCase());
    roleNavigate(`/care-coordination-form/${actionCase.id}`);
    return;
  }
  const previousImages = await buildPrefillImages(previousCase);
  const values = buildPrefillFormValues(previousCase, actionCase.type);
  if (previousImages.length) {
    values.push({ form_question_id: 17, value: { images: previousImages } });
  }

  dispatch(setForm(values));
  dispatch(
    setLinkedCase({
      linkedCaseId: actionCase.id,
      linkedCasePatientId: actionCase.patient_id,
      // Falls back to true (assume payment may still be owed) when the intake
      // lookup itself fails — safer than silently treating a failed lookup as
      // "definitely free" and stripping payment_profile_id from the PATCH.
      linkedCaseNeedsPayment: intakeState?.upcoming_payment_required ?? true,
      previousClinicalData: extractPreviousClinicalData(previousCase),
    }),
  );

  roleNavigate('/select-patient', {
    state: { currentUserId: actionCase.patient_id?.toString() },
  });
};
